import { useState } from 'react';
import { trpc } from '../lib/trpc';
import { CreateToolModal } from '../components/CreateToolModal';
import { EditToolModal } from '../components/EditToolModal';
import { TestToolModal } from '../components/TestToolModal';

/**
 * Category definition for filtering.
 */
interface Category {
  id: string;
  label: string;
  count: number;
}

/**
 * Tool data structure from the API.
 */
interface ToolData {
  id: string;
  name: string;
  description: string | null;
  toolType: string;
  configSchema?: unknown;
  status: string | null;
  createdAt: string | null;
}

/**
 * Tools page component.
 *
 * Displays tool registry with management capabilities.
 * Allows administrators to view all tools and create new prompt-based tools.
 */
export function ToolsPage() {
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [selectedTool, setSelectedTool] = useState<ToolData | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isTestModalOpen, setIsTestModalOpen] = useState(false);
  const [editingTool, setEditingTool] = useState<ToolData | null>(null);
  const [testingTool, setTestingTool] = useState<ToolData | null>(null);

  const toolsQuery = trpc.tools.list.useQuery({ includeAll: true });
  const utils = trpc.useUtils();

  const createToolMutation = trpc.tools.create.useMutation({
    onSuccess: () => {
      utils.tools.list.invalidate();
      setIsCreateModalOpen(false);
    },
  });

  const updateStatusMutation = trpc.tools.updateStatus.useMutation({
    onSuccess: () => {
      utils.tools.list.invalidate();
    },
  });

  const updateToolMutation = trpc.tools.update.useMutation({
    onSuccess: (data) => {
      utils.tools.list.invalidate();
      setIsEditModalOpen(false);
      setEditingTool(null);
      // Update selected tool if it's the one being edited
      if (selectedTool?.id === data.tool.id) {
        setSelectedTool(data.tool);
      }
    },
  });

  const testPromptMutation = trpc.tools.testPrompt.useMutation();

  // Filter tools by search and category
  const filteredTools = toolsQuery.data?.tools.filter((tool) => {
    // Search filter
    const matchesSearch = !search ||
      tool.name.toLowerCase().includes(search.toLowerCase()) ||
      (tool.description?.toLowerCase().includes(search.toLowerCase()));

    // Category filter
    let matchesCategory = true;
    if (categoryFilter === 'hardcoded') {
      matchesCategory = tool.toolType === 'hardcoded';
    } else if (categoryFilter === 'prompt-based') {
      matchesCategory = tool.toolType === 'prompt-based';
    } else if (categoryFilter === 'active') {
      matchesCategory = tool.status === 'active';
    } else if (categoryFilter === 'inactive') {
      matchesCategory = tool.status === 'inactive' || tool.status === 'deprecated';
    }

    return matchesSearch && matchesCategory;
  });

  // Generate categories from tool data
  const categories: Category[] = [
    { id: 'all', label: 'All Tools', count: toolsQuery.data?.tools.length ?? 0 },
    { id: 'hardcoded', label: 'Hardcoded', count: toolsQuery.data?.tools.filter(t => t.toolType === 'hardcoded').length ?? 0 },
    { id: 'prompt-based', label: 'Prompt-based', count: toolsQuery.data?.tools.filter(t => t.toolType === 'prompt-based').length ?? 0 },
    { id: 'active', label: 'Active', count: toolsQuery.data?.tools.filter(t => t.status === 'active').length ?? 0 },
    { id: 'inactive', label: 'Inactive', count: toolsQuery.data?.tools.filter(t => t.status === 'inactive' || t.status === 'deprecated').length ?? 0 },
  ];

  const handleCreateTool = async (data: { name: string; description?: string; promptDefinition: string }) => {
    await createToolMutation.mutateAsync(data);
  };

  const handleToggleStatus = async (tool: ToolData) => {
    const newStatus = tool.status === 'active' ? 'inactive' : 'active';
    await updateStatusMutation.mutateAsync({ id: tool.id, status: newStatus });
  };

  const handleEditTool = (tool: ToolData) => {
    setEditingTool(tool);
    setIsEditModalOpen(true);
  };

  const handleTestTool = (tool: ToolData) => {
    setTestingTool(tool);
    setIsTestModalOpen(true);
  };

  const handleUpdateTool = async (data: { id: string; name?: string; description?: string | null; promptDefinition?: string }) => {
    await updateToolMutation.mutateAsync(data);
  };

  const handleTestPrompt = async (data: { toolId: string; input: string }) => {
    return await testPromptMutation.mutateAsync(data);
  };

  const getToolTypeColor = (toolType: string): { bg: string; text: string } => {
    return toolType === 'hardcoded'
      ? { bg: 'rgba(90, 138, 94, 0.1)', text: '#5A8A5E' }
      : { bg: 'rgba(90, 106, 138, 0.1)', text: '#5A6A8A' };
  };

  const getStatusColor = (status: string | null): { bg: string; text: string } => {
    switch (status) {
      case 'active':
        return { bg: 'rgba(90, 138, 94, 0.15)', text: '#5A8A5E' };
      case 'inactive':
        return { bg: 'rgba(139, 90, 80, 0.15)', text: '#8B5A50' };
      case 'deprecated':
        return { bg: 'rgba(93, 86, 78, 0.15)', text: '#5C564E' };
      default:
        return { bg: 'rgba(93, 86, 78, 0.15)', text: '#5C564E' };
    }
  };

  const getPromptDefinition = (tool: ToolData): string | null => {
    if (tool.toolType !== 'prompt-based' || !tool.configSchema) return null;
    const schema = tool.configSchema as { promptDefinition?: string };
    return schema.promptDefinition || null;
  };

  return (
    <div style={styles.container}>
      {/* Create Tool Modal */}
      <CreateToolModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={handleCreateTool}
        isSubmitting={createToolMutation.isPending}
        error={createToolMutation.error?.message}
      />

      {/* Edit Tool Modal */}
      <EditToolModal
        isOpen={isEditModalOpen}
        tool={editingTool}
        onClose={() => {
          setIsEditModalOpen(false);
          setEditingTool(null);
        }}
        onSubmit={handleUpdateTool}
        isSubmitting={updateToolMutation.isPending}
        error={updateToolMutation.error?.message}
      />

      {/* Test Tool Modal */}
      <TestToolModal
        isOpen={isTestModalOpen}
        tool={testingTool}
        onClose={() => {
          setIsTestModalOpen(false);
          setTestingTool(null);
        }}
        onTest={handleTestPrompt}
        isSubmitting={testPromptMutation.isPending}
        error={testPromptMutation.error?.message}
      />

      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Tools</h1>
          <p style={styles.subtitle}>Tool registry and custom tool management</p>
        </div>
        <button style={styles.createButton} onClick={() => setIsCreateModalOpen(true)}>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M7 1V13M1 7H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
          Create Tool
        </button>
      </div>

      {/* Category Filters */}
      <div style={styles.categoryFilters}>
        {categories.map(cat => (
          <button
            key={cat.id}
            onClick={() => setCategoryFilter(cat.id)}
            style={{
              ...styles.categoryButton,
              ...(categoryFilter === cat.id ? styles.categoryButtonActive : styles.categoryButtonInactive),
            }}
          >
            {cat.label}
            <span style={{
              ...styles.categoryCount,
              background: categoryFilter === cat.id ? 'rgba(255,255,255,0.2)' : 'rgba(93, 86, 78, 0.08)',
            }}>
              {cat.count}
            </span>
          </button>
        ))}
      </div>

      {/* Search Input */}
      <div style={styles.searchContainer}>
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={styles.searchIcon}>
          <circle cx="7" cy="7" r="5.5" stroke="#9B9285" strokeWidth="1.5"/>
          <path d="M11 11L14 14" stroke="#9B9285" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
        <input
          type="text"
          placeholder="Search tools..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={styles.searchInput}
        />
      </div>

      {/* Content */}
      {toolsQuery.isLoading ? (
        <div style={styles.loading}>Loading tools...</div>
      ) : toolsQuery.error ? (
        <div style={styles.error}>
          Error loading tools: {toolsQuery.error.message}
        </div>
      ) : filteredTools?.length === 0 ? (
        <div style={styles.emptyState}>
          <div style={styles.emptyIcon}>🔧</div>
          <h3 style={styles.emptyTitle}>No tools found</h3>
          <p style={styles.emptyText}>
            {categoryFilter !== 'all'
              ? `No ${categoryFilter} tools available.`
              : 'Create your first tool to extend bot capabilities.'
            }
          </p>
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: selectedTool ? '1fr 380px' : '1fr',
          gap: '20px',
        }}>
          {/* Tools Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: selectedTool ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
            gap: '16px',
            alignContent: 'start',
          }}>
            {filteredTools?.map((tool) => {
              const toolData: ToolData = {
                id: tool.id,
                name: tool.name,
                description: tool.description,
                toolType: tool.toolType,
                configSchema: tool.configSchema,
                status: tool.status,
                createdAt: tool.createdAt,
              };
              const isSelected = selectedTool?.id === tool.id;
              const typeColor = getToolTypeColor(tool.toolType);
              const statusColor = getStatusColor(tool.status);

              return (
                <div
                  key={tool.id}
                  onClick={() => setSelectedTool(toolData)}
                  style={{
                    ...styles.toolCard,
                    border: isSelected ? '2px solid #5A8A5E' : '1px solid rgba(93, 86, 78, 0.06)',
                  }}
                >
                  {/* Card Header */}
                  <div style={styles.cardHeader}>
                    <div style={styles.cardHeaderLeft}>
                      <span style={styles.toolIcon}>
                        {tool.toolType === 'hardcoded' ? '🔩' : '✨'}
                      </span>
                      <div>
                        <h3 style={styles.toolName}>{tool.name}</h3>
                        <div style={styles.badgeContainer}>
                          <span style={{
                            ...styles.typeBadge,
                            background: typeColor.bg,
                            color: typeColor.text,
                          }}>
                            {tool.toolType === 'hardcoded' ? 'Hardcoded' : 'Prompt'}
                          </span>
                          <span style={{
                            ...styles.statusBadge,
                            background: statusColor.bg,
                            color: statusColor.text,
                          }}>
                            {tool.status}
                          </span>
                        </div>
                      </div>
                    </div>
                    {/* Status Toggle */}
                    <div
                      style={{
                        ...styles.toggle,
                        background: tool.status === 'active' ? '#5A8A5E' : 'rgba(93, 86, 78, 0.15)',
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleStatus(toolData);
                      }}
                    >
                      <div style={{
                        ...styles.toggleKnob,
                        left: tool.status === 'active' ? '16px' : '2px',
                      }} />
                    </div>
                  </div>

                  {/* Description */}
                  <p style={styles.toolDescription}>
                    {tool.description
                      ? (tool.description.length > 100 ? `${tool.description.slice(0, 100)}...` : tool.description)
                      : 'No description provided'
                    }
                  </p>

                  {/* Card Footer */}
                  <div style={styles.cardFooter}>
                    <span style={styles.createdAt}>
                      {tool.createdAt ? new Date(tool.createdAt).toLocaleDateString() : 'Unknown date'}
                    </span>
                    <span style={styles.toolId}>
                      {tool.id.slice(0, 8)}...
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Detail Panel */}
          {selectedTool && (
            <ToolDetailPanel
              tool={selectedTool}
              onClose={() => setSelectedTool(null)}
              onToggleStatus={() => handleToggleStatus(selectedTool)}
              onEdit={() => handleEditTool(selectedTool)}
              onTest={() => handleTestTool(selectedTool)}
              promptDefinition={getPromptDefinition(selectedTool)}
            />
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Tool Detail Panel Component
 */
interface ToolDetailPanelProps {
  tool: ToolData;
  onClose: () => void;
  onToggleStatus: () => void;
  onEdit: () => void;
  onTest: () => void;
  promptDefinition: string | null;
}

function ToolDetailPanel({ tool, onClose, onToggleStatus, onEdit, onTest, promptDefinition }: ToolDetailPanelProps) {
  const typeColor = tool.toolType === 'hardcoded'
    ? { bg: 'rgba(90, 138, 94, 0.1)', text: '#5A8A5E' }
    : { bg: 'rgba(90, 106, 138, 0.1)', text: '#5A6A8A' };

  return (
    <div style={styles.detailPanel}>
      {/* Panel Header */}
      <div style={styles.panelHeader}>
        <div style={styles.panelHeaderLeft}>
          <span style={styles.panelIcon}>
            {tool.toolType === 'hardcoded' ? '🔩' : '✨'}
          </span>
          <div>
            <h2 style={styles.panelTitle}>{tool.name}</h2>
            <div style={styles.panelBadges}>
              <span style={{
                ...styles.panelTypeBadge,
                background: typeColor.bg,
                color: typeColor.text,
              }}>
                {tool.toolType === 'hardcoded' ? 'Hardcoded' : 'Prompt-based'}
              </span>
            </div>
          </div>
        </div>
        <div style={styles.panelHeaderButtons}>
          {tool.toolType === 'prompt-based' && (
            <>
              <button onClick={onTest} style={styles.testButton} title="Test tool">
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M7 1L7 13M1 7L13 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" transform="rotate(45 7 7)"/>
                  <circle cx="7" cy="7" r="5" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                </svg>
              </button>
              <button onClick={onEdit} style={styles.editButton} title="Edit tool">
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M10.5 1.75L12.25 3.5M1.75 12.25L2.625 9.188L9.625 2.188L11.812 4.375L4.812 11.375L1.75 12.25Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </>
          )}
          <button onClick={onClose} style={styles.closeButton}>×</button>
        </div>
      </div>

      {/* Status Section */}
      <div style={styles.statusSection}>
        <div>
          <p style={styles.statusLabel}>Status</p>
          <p style={styles.statusDescription}>
            {tool.status === 'active'
              ? 'Tool is enabled and available for bots'
              : 'Tool is disabled and not available'}
          </p>
        </div>
        <div style={styles.statusButtons}>
          <button
            onClick={onToggleStatus}
            style={{
              ...styles.statusButton,
              borderColor: tool.status === 'active' ? '#5A8A5E' : '#8B5A50',
              background: tool.status === 'active' ? 'rgba(90, 138, 94, 0.1)' : 'rgba(139, 90, 80, 0.1)',
              color: tool.status === 'active' ? '#5A8A5E' : '#8B5A50',
            }}
          >
            {tool.status === 'active' ? '✓ Active' : '✗ Inactive'}
          </button>
        </div>
      </div>

      {/* Description */}
      <div style={styles.descriptionSection}>
        <p style={styles.sectionLabel}>Description</p>
        <p style={styles.panelDescription}>
          {tool.description || 'No description provided for this tool.'}
        </p>
      </div>

      {/* Prompt Definition (for prompt-based tools) */}
      {promptDefinition && (
        <div style={styles.promptSection}>
          <p style={styles.sectionLabel}>AI Prompt Definition</p>
          <div style={styles.promptContainer}>
            <pre style={styles.promptText}>{promptDefinition}</pre>
          </div>
        </div>
      )}

      {/* Config Schema Preview */}
      {tool.configSchema !== undefined && tool.configSchema !== null ? (
        <div style={styles.schemaSection}>
          <p style={styles.sectionLabel}>Configuration Schema</p>
          <div style={styles.schemaContainer}>
            <pre style={styles.schemaText}>
              {JSON.stringify(tool.configSchema, null, 2)}
            </pre>
          </div>
        </div>
      ) : null}

      {/* Metadata */}
      <div style={styles.metadataSection}>
        <p style={styles.sectionLabel}>Metadata</p>
        <div style={styles.metadataGrid}>
          <div style={styles.metadataItem}>
            <span style={styles.metadataKey}>Tool ID</span>
            <span style={styles.metadataValue}>{tool.id}</span>
          </div>
          <div style={styles.metadataItem}>
            <span style={styles.metadataKey}>Type</span>
            <span style={styles.metadataValue}>{tool.toolType}</span>
          </div>
          <div style={styles.metadataItem}>
            <span style={styles.metadataKey}>Created</span>
            <span style={styles.metadataValue}>
              {tool.createdAt ? new Date(tool.createdAt).toLocaleString() : 'Unknown'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    maxWidth: '1400px',
    padding: '32px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
  },
  title: {
    fontFamily: '"Sentient", Georgia, serif',
    fontSize: '24px',
    fontWeight: 600,
    color: '#3D3A36',
    marginBottom: '4px',
  },
  subtitle: {
    fontSize: '14px',
    color: '#9B9285',
  },
  createButton: {
    padding: '12px 20px',
    borderRadius: '10px',
    background: '#3D3A36',
    border: 'none',
    color: '#FAF9F7',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  categoryFilters: {
    display: 'flex',
    gap: '8px',
    marginBottom: '20px',
    flexWrap: 'wrap',
  },
  categoryButton: {
    padding: '8px 16px',
    borderRadius: '100px',
    fontSize: '13px',
    fontWeight: 500,
    cursor: 'pointer',
    fontFamily: 'inherit',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  categoryButtonActive: {
    border: 'none',
    background: '#3D3A36',
    color: '#FAF9F7',
  },
  categoryButtonInactive: {
    border: '1px solid rgba(93, 86, 78, 0.15)',
    background: 'white',
    color: '#5C564E',
  },
  categoryCount: {
    padding: '2px 6px',
    borderRadius: '100px',
    fontSize: '11px',
  },
  searchContainer: {
    position: 'relative',
    marginBottom: '24px',
    maxWidth: '400px',
  },
  searchIcon: {
    position: 'absolute',
    left: '14px',
    top: '50%',
    transform: 'translateY(-50%)',
  },
  searchInput: {
    width: '100%',
    padding: '12px 16px 12px 44px',
    border: '1px solid rgba(93, 86, 78, 0.15)',
    borderRadius: '10px',
    fontSize: '14px',
    fontFamily: 'inherit',
    outline: 'none',
    background: 'white',
  },
  loading: {
    padding: '60px 20px',
    textAlign: 'center',
    color: '#9B9285',
    fontSize: '14px',
  },
  error: {
    padding: '16px 20px',
    backgroundColor: 'rgba(139, 90, 80, 0.1)',
    color: '#8B5A50',
    borderRadius: '10px',
    fontSize: '14px',
  },
  emptyState: {
    textAlign: 'center',
    padding: '80px 20px',
    backgroundColor: 'white',
    borderRadius: '16px',
    border: '1px solid rgba(93, 86, 78, 0.06)',
  },
  emptyIcon: {
    fontSize: '48px',
    marginBottom: '16px',
  },
  emptyTitle: {
    fontSize: '18px',
    fontWeight: 600,
    color: '#3D3A36',
    marginBottom: '8px',
  },
  emptyText: {
    fontSize: '14px',
    color: '#9B9285',
  },
  toolCard: {
    background: 'white',
    borderRadius: '14px',
    padding: '20px',
    cursor: 'pointer',
    transition: 'border-color 0.15s ease',
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: '12px',
  },
  cardHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  toolIcon: {
    fontSize: '20px',
  },
  toolName: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#3D3A36',
    margin: 0,
  },
  badgeContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    marginTop: '4px',
  },
  typeBadge: {
    padding: '2px 6px',
    borderRadius: '4px',
    fontSize: '9px',
    fontWeight: 500,
  },
  statusBadge: {
    padding: '2px 6px',
    borderRadius: '4px',
    fontSize: '9px',
    fontWeight: 500,
    textTransform: 'capitalize',
  },
  toggle: {
    width: '32px',
    height: '18px',
    borderRadius: '9px',
    position: 'relative',
    cursor: 'pointer',
  },
  toggleKnob: {
    width: '14px',
    height: '14px',
    borderRadius: '50%',
    background: 'white',
    position: 'absolute',
    top: '2px',
    boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
    transition: 'left 0.15s ease',
  },
  toolDescription: {
    fontSize: '11px',
    color: '#706A62',
    marginBottom: '12px',
    lineHeight: '1.5',
  },
  cardFooter: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  createdAt: {
    fontSize: '10px',
    color: '#9B9285',
  },
  toolId: {
    fontSize: '10px',
    color: '#9B9285',
    fontFamily: 'monospace',
  },
  // Detail Panel Styles
  detailPanel: {
    background: 'white',
    borderRadius: '16px',
    padding: '24px',
    border: '1px solid rgba(93, 86, 78, 0.06)',
    position: 'sticky',
    top: '24px',
    alignSelf: 'start',
    maxHeight: 'calc(100vh - 120px)',
    overflowY: 'auto',
  },
  panelHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '16px',
  },
  panelHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  panelIcon: {
    fontSize: '28px',
  },
  panelTitle: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#3D3A36',
    margin: 0,
  },
  panelBadges: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    marginTop: '4px',
  },
  panelTypeBadge: {
    padding: '3px 8px',
    borderRadius: '4px',
    fontSize: '10px',
    fontWeight: 500,
  },
  closeButton: {
    width: '28px',
    height: '28px',
    borderRadius: '8px',
    border: 'none',
    background: 'rgba(93, 86, 78, 0.06)',
    cursor: 'pointer',
    color: '#706A62',
    fontSize: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  panelHeaderButtons: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  testButton: {
    width: '28px',
    height: '28px',
    borderRadius: '8px',
    border: 'none',
    background: 'rgba(90, 138, 94, 0.1)',
    cursor: 'pointer',
    color: '#5A8A5E',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background 0.15s ease',
  },
  editButton: {
    width: '28px',
    height: '28px',
    borderRadius: '8px',
    border: 'none',
    background: 'rgba(90, 106, 138, 0.1)',
    cursor: 'pointer',
    color: '#5A6A8A',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background 0.15s ease',
  },
  statusSection: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 14px',
    borderRadius: '10px',
    background: 'rgba(93, 86, 78, 0.03)',
    marginBottom: '16px',
  },
  statusLabel: {
    fontSize: '12px',
    fontWeight: 600,
    color: '#3D3A36',
    marginBottom: '2px',
    margin: 0,
  },
  statusDescription: {
    fontSize: '11px',
    color: '#9B9285',
    margin: 0,
  },
  statusButtons: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  statusButton: {
    padding: '6px 12px',
    borderRadius: '6px',
    border: '1px solid',
    fontSize: '11px',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  descriptionSection: {
    marginBottom: '16px',
  },
  sectionLabel: {
    fontSize: '10px',
    color: '#9B9285',
    textTransform: 'uppercase',
    fontWeight: 600,
    marginBottom: '8px',
    margin: '0 0 8px 0',
  },
  panelDescription: {
    fontSize: '12px',
    color: '#5C564E',
    lineHeight: '1.6',
    margin: 0,
  },
  promptSection: {
    marginBottom: '16px',
  },
  promptContainer: {
    padding: '12px',
    background: 'linear-gradient(135deg, rgba(90, 106, 138, 0.08) 0%, rgba(90, 138, 94, 0.08) 100%)',
    borderRadius: '8px',
    border: '1px solid rgba(90, 106, 138, 0.15)',
  },
  promptText: {
    fontSize: '11px',
    color: '#5C564E',
    lineHeight: '1.6',
    margin: 0,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    fontFamily: 'inherit',
  },
  schemaSection: {
    marginBottom: '16px',
  },
  schemaContainer: {
    padding: '12px',
    background: 'rgba(93, 86, 78, 0.03)',
    borderRadius: '8px',
    overflow: 'auto',
    maxHeight: '200px',
  },
  schemaText: {
    fontSize: '10px',
    color: '#5C564E',
    lineHeight: '1.5',
    margin: 0,
    fontFamily: 'monospace',
  },
  metadataSection: {
    marginBottom: '0',
  },
  metadataGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  metadataItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 10px',
    background: 'rgba(93, 86, 78, 0.03)',
    borderRadius: '6px',
  },
  metadataKey: {
    fontSize: '11px',
    color: '#706A62',
  },
  metadataValue: {
    fontSize: '11px',
    fontWeight: 500,
    color: '#3D3A36',
    maxWidth: '200px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
};

export default ToolsPage;
